import './Screen.scss';
import { combineLatest, ReplaySubject, share, takeUntil } from 'rxjs';
import { debounceTime, filter, switchMap, take } from 'rxjs/operators';
import { LoadingThrobber } from '@components/Utility/LoadingThrobber/LoadingThrobber.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { copyToClipboard, getID } from '@components/Utility/index.js';
import { QStrapContainer } from '@components/Component/Container/QStrapContainer/QStrapContainer.js';
import { ENV } from '@components/Environment.js';
import { eventContextGet } from '@components/EventConfigManager/EventConfigManagerUtil.js';
/**
 * The abstract class `Screen` extends the `Container` class.
 * It serves as a base class for different types of screens in the application.
 */
export class Screen extends QStrapContainer {
    set currentZIndex(val) {
        if (val > 500) {
            this.resetZIndexes();
            return;
        }
        Screen._currentZIndex = val;
    }
    get currentZIndex() {
        return Screen._currentZIndex;
    }
    get currentForm() {
        return this._currentForm;
    }
    set currentForm(value) {
        if (this._currentForm && this._currentForm !== value) {
            // we had an old form in place, remove it
            this.formRemove(this._currentForm);
            if (value) {
                // add the new form
                this.componentAdd(value);
            }
        }
        else if (value && !this._currentForm) {
            // we got a new form and we dont currently have one, add the new form
            this.componentAdd(value);
        }
        this._propertySet('currentForm', value);
        if (this.drawn) {
            this.renderForm();
        }
    }
    get title() {
        return this._title;
    }
    set title(value) {
        this._propertySet('title', value);
        this.renderTitle();
    }
    /**
     * Utility class to load form assets when the screen route changes
     * @private
     */
    get ScreenRouter() {
        if (!this.dependencies.ScreenRouter) {
            this.dependencies.ScreenRouter = DIContainer.getInstance('ScreenRouter');
        }
        return this.dependencies.ScreenRouter;
    }
    /**
     * Access to the containing ScreenManager.
     * @constructor
     */
    get ScreenManager() {
        if (!this.dependencies.ScreenManager) {
            // we didnt receive a request dependency injection, so just use the global singleton
            return DIContainer.getInstance('ScreenManager');
            // this.dependencies.ScreenManager = // dont set the dependency in this case because screen manager can be swapped out.
        }
        return this.dependencies.ScreenManager;
    }
    /**
     * Constructs an instance of `Screen`.
     * @param {string} id - The ID of the screen.
     * @param {HTMLElement} rootElement - The root element for the screen to draw in.
     * @param dependencies
     * @param {string} screenGroupId - The ID of the screen group to which the screen belongs.
     */
    constructor(id, rootElement, dependencies = {}, screenGroupId = '') {
        super(id, rootElement, dependencies);
        this.componentName = 'Screen';
        this.hotkeys = {
            ...this.hotkeys // we are extending the hotkeys from the parent class. 
            ,
            'ctrl+alt+f': () => {
                DIContainer.getInstance('Application').openFullscreen(this.contentContainer);
                setTimeout(() => {
                    this.currentForm?.handleResize();
                }, 100);
                return true;
            }
        };
        this.screenMenuIcon = 'expand_more';
        /** The currently active form */
        this._currentForm = null;
        /** The title of this screen (tab/window frame caption) */
        this._title = '';
        this.screenResize$ = new ReplaySubject(1);
        this.screenGroupId = screenGroupId;
    }
    draw() {
        super.draw();
        this.rootElement.classList.add('s360Screen');
        this.rootElement.setAttribute('data-component-id', this.id);
        if (!this.contentContainer) {
            this.contentContainer = this.rootElement.querySelector('.screen-content');
            this.loadingThrobberRoot = this.contentContainer;
        }
        if (!this.screenTitleContainer) {
            this.screenTitleContainer = this.rootElement.querySelector('.screen-title');
        }
        this.registerEvent('mousedown', this.rootElement, () => {
            this.activateScreen();
        });
        if (this._currentForm) {
            this.renderForm();
        } /*else if (this._errorMessage) {
            this.renderError();
        }*/
        if (this._title) {
            this.renderTitle();
        }
        this.updateScreenBreakpoint();
        this.screenResize$
            .pipe(debounceTime(50) //likely will need this but maybe not.
        , takeUntil(this.destroy$))
            .subscribe(() => {
            this.handleResize();
        });
        const resizeMgr = DIContainer.getInstance('ResizeManager');
        resizeMgr.observe(this.rootElement, (element) => {
            this.screenResize$.next({
                width: element.contentRect.width,
                height: element.contentRect.height
            });
        });
    }
    /**
     * Updates the screen.title property and immediately updates the DOM if the screen has been drawn already.
     */
    renderTitle() {
        if (this.screenTitleContainer) {
            this.screenTitleContainer.textContent = this._title;
            this.registerTooltip(this.screenTitleContainer, {
                tooltip: this._title,
                delay: 400,
                sidePriority: ['top', 'bottom', 'right', 'left'],
                maxDistanceFromMouse: 50
            });
        }
    }
    /**
     * Renders errorContainer if the screen has drawn and error is not empty.
     * @protected
     */
    /*renderError() {
        if (!this.contentContainer) {
            return;
        }
        
        const error = this._errorMessage;
        
        if (!error && this.errorContainer) {
            this.errorContainer.remove();
            this.errorContainer = null;
        } else if (error) {
            
            if (!this.errorContainer) {
                this.errorContainer = document.createElement('div');
                this.errorContainer.classList.add('error-container');
                
                const errorIcon = DIContainer.getInstance<IconManagerInterface>('IconManager').getIconElement('error');
                errorIcon.classList.add('error-icon');
                this.errorContainer.appendChild(errorIcon);
                
                const errorMessage = document.createElement('p');
                errorMessage.classList.add('error-message');
                errorMessage.textContent = error;
                this.errorContainer.appendChild(errorMessage);
            } else {
                const errorMessage = this.errorContainer.querySelector('.error-message') as HTMLElement;
                errorMessage.textContent = error;
            }
            
            this.contentContainer.appendChild(this.errorContainer);
        }
    }*/
    /**
     * Add a `Form` object to a Screen, circumventing the `FormLoader`. Typically used in single screen apps like `Login`.
     * @param form
     */
    loadForm(form) {
        this.currentForm = form;
    }
    /**
     * Remove the `Form` from the screen without destroying it. Typically used in single screen apps like `Login`.
     */
    unloadForm() {
        const form = this.currentForm;
        if (!form) {
            return;
        }
        this.componentRemove(form.id);
        form.rootElement?.remove();
        this._currentForm = null; // we dont want the form to get destroyed by formRemove which would get called in currentForm set handler.
        this.currentForm = null; // trigger our set handler.
        return form;
    }
    /**
     * Render a form on the screen.
     */
    renderForm() {
        if (!this.contentContainer) {
            return;
        }
        const form = this.currentForm;
        if (!form) {
            return;
        }
        if (!form.rootElement) {
            form.rootElement = document.createElement(form.rootElementType);
        }
        if (!form.drawn) {
            form.draw();
        }
        this.contentContainer.appendChild(form.rootElement);
        if (this.formTitleSubscription) {
            this.formTitleSubscription.unsubscribe();
        }
        this.formTitleSubscription = combineLatest([
            form.propertyGet$('title'),
            form.propertyGet$('recordState')
        ])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([title, state]) => {
            if (form.hasUnSavedChanges) {
                title = '* ' + title;
            }
            this.title = title;
        });
        setTimeout(() => {
            form.handleResize();
        }, 0);
    }
    /**
     * remove a form component from the screen and destroy it
     * @param form
     */
    formRemove(form) {
        this.componentRemove(form.id);
        form.rootElement.remove();
        form.destroy();
    }
    generateChildTemplate() {
        const template = '';
        return template;
    }
    /**
     * Redirect the screen to a new url. Uses `ScreenRouter` to retrieve form definition and load it into the screen.
     * @param url
     * @param parameters
     * @param options
     */
    navigate(url, parameters, options) {
        const previousForm = this.currentForm;
        if (previousForm) {
            if (DIContainer.isSingletonRegistered(previousForm.componentName)) {
                this.unloadForm();
            }
            else {
                this.currentForm = null;
            }
        }
        this.handleProgress(0);
        const obs = this.ScreenRouter.loadRoute$(url, parameters, options).pipe(share());
        obs.subscribe({
            next: (form) => {
                console.log('Form Object Loaded:', form);
                this.currentForm = form;
                this.handleProgress(100);
            },
            error: (error) => {
                // this.errorMessage = error.message;
                this.title = 'Fatal Error';
                this.navigate('error', {
                    error,
                    message: error.message
                });
            }
        });
        // if (this.errorMessage) {
        // 	this.errorMessage = '';
        // }
        return obs;
    }
    /**
     * Activates the screen by setting it as the active screen in the `ScreenManager`.
     */
    activateScreen() {
        this.ScreenManager.activeScreenSet(this.id);
    }
    /**
     * Sets the screen as active or inactive.
     * @param {boolean} b - A boolean indicating whether to set the screen as active.
     * @returns The instance of the screen for chaining.
     */
    activeSet(b) {
        if (b) {
            // prevent inflating the z-index when you focus a form that's already active
            if (!this.rootElement.classList.contains('active')) {
                this.rootElement.classList.add('active');
                this.rootElement.style.zIndex = `${this.currentZIndex++}`; // Bring to front when clicked/opened
            }
        }
        else {
            this.rootElement.classList.remove('active');
        }
        return this;
    }
    /**
     * Closes the screen by removing it from the `ScreenManager` and destroying it.
     */
    closeScreen() {
        if (this.currentForm) {
            this.currentForm.handleFormClose().pipe(take(1), filter(b => b))
                .subscribe(() => {
                this.removeScreen();
            });
        }
        else {
            this.removeScreen();
        }
    }
    removeScreen() {
        this.ScreenManager.screenRemove(this.id);
        this.destroy();
    }
    /**
     * Event handler runs when the screen resizes. Update QStrap breakpoint and inform the currentForm to resize its components.
     */
    handleResize() {
        this.updateScreenBreakpoint();
        if (this.currentForm && this.contentContainer) {
            this.currentForm.handleResize();
        }
    }
    childContainerGet() {
        if (!this.contentContainer) {
            this.contentContainer = this.rootElement.querySelector('.screen-content');
            this.loadingThrobberRoot = this.contentContainer;
        }
        return this.contentContainer;
    }
    createLoadingThrobber() {
        if (!this.loadingThrobber) {
            // only create an instance of throbber after the first time the component needs to show progress
            this.loadingThrobber = new LoadingThrobber(getID(this.id), document.createElement('div'));
            this.loadingThrobber.draw();
            this.appendLoadingThrobber();
        }
    }
    appendLoadingThrobber() {
        if (this.contentContainer) {
            super.appendLoadingThrobber();
        }
    }
    // protected appendLoadingThrobber() {
    // 	if (this.contentContainer && this.loadingThrobber) {
    // 		this.contentContainer!.style.position = 'relative'; // throbber position is absolute so its parent must be relative to position it correctly to cover the component that is loading.
    // 		this.contentContainer!.appendChild(this.loadingThrobber.rootElement);
    // 	}
    // }
    displayScreenMenu(event) {
        const contextInfo = {
            targetElement: event.target,
            event: event,
            initiator: this
        };
        const items = this.getContextMenuItems(contextInfo);
        DIContainer.getInstance('ContextMenu').showMenu(items, contextInfo, `${this.globalId}_menu`);
    }
    getContextMenuItems(_ctx) {
        const items = [];
        items.push({
            caption: 'Copy URL',
            icon: 'content_copy',
            onClick: () => {
                if (this.currentForm) {
                    copyToClipboard(this.currentForm.getCurrentUrl());
                }
            }
        });
        items.push({
            caption: 'Copy Formatted URL',
            icon: 'content_copy',
            onClick: () => {
                if (this.currentForm) {
                    const url = this.currentForm.getCurrentUrl();
                    const title = this.title;
                    copyToClipboard(url, `<a href="${url}">${title}</a>`);
                }
            }
        });
        const conditionsManager = DIContainer.getInstance('ConditionsManager');
        if (ENV('appName') === 'q360' && conditionsManager.checkPerm('user_employee') && this.currentForm) {
            items.push({
                caption: 'Add Bookmark',
                icon: 'bookmark_add',
                onClick: (contextInfo) => {
                    DIContainer.getInstance('Application').componentGet('toolbar').createBookmark(contextInfo.event, { createParameters: { screen: this.id } });
                }
            });
            items.push({
                caption: 'Set Reminder',
                icon: 'calendar_add_on',
                onClick: () => {
                    DIContainer.resolveLazy('SetReminderModal')
                        .pipe(switchMap((modal) => {
                        return modal.open({
                            attachToElement: this.currentForm,
                            url: this.currentForm.getCurrentUrl(),
                            title: this.title
                        });
                    })).subscribe((reminderRecord) => {
                    });
                }
            });
        }
        if (conditionsManager.checkPerm('helpadmin_create') && this.currentForm) {
            items.push({
                caption: 'Add Anchor',
                icon: 'help',
                onClick: () => {
                    const eventManager = DIContainer.getInstance('EventConfigManager');
                    eventManager.parse([{
                            action: 'createRecord',
                            params: {
                                tableName: 'helpanchor',
                                target: '_top',
                                createParameters: {},
                                additionalData: {
                                    form: this.currentForm.formName
                                }
                            }
                        }], eventContextGet({ initiator: this })).subscribe();
                }
            });
        }
        const routes = this.currentForm?.routes || [];
        if (routes.length > 1) {
            const routeItems = [];
            routes.forEach((r) => {
                routeItems.push({
                    id: r.altformname,
                    caption: r.name,
                    icon: this.currentForm.routeFormName === r.altformname ? 'check' : '',
                    onClick: () => {
                        if (this.currentForm.routeFormName !== r.altformname) {
                            const params = {
                                ...this.currentForm.parameters,
                                _view: r.altformname
                            };
                            this.navigate(`/form/${r.formname}`, params);
                        }
                    }
                });
            });
            routeItems.push({
                id: 'savedesktop',
                icon: 'save',
                caption: 'Save As Default Desktop View',
                onClick: () => {
                    this.currentForm.saveUserPreference('form', 'desktopRouteName', this.currentForm.routeFormName).subscribe();
                }
            });
            routeItems.push({
                id: 'savemobile',
                icon: 'save',
                caption: 'Save As Default Mobile View',
                onClick: () => {
                    this.currentForm.saveUserPreference('form', 'mobileRouteName', this.currentForm.routeFormName).subscribe();
                }
            });
            items.push({
                caption: 'Change View...',
                icon: 'view_quilt',
                children: routeItems
            });
        }
        items.push({
            caption: '',
            itemtype: 'SEPARATOR'
        });
        if (this.ScreenManager.screenCountGet() > 1) {
            items.push({
                caption: 'Close Other Forms',
                icon: 'tab_close'
                // ,hotkey: 'escape'
                ,
                onClick: () => {
                    const sm = this.ScreenManager;
                    sm.screensList().forEach(({ id }) => {
                        if (sm.screenGet(id) !== this) {
                            sm.screenGet(id)?.closeScreen();
                        }
                    });
                }
            });
        }
        items.push({
            caption: 'Close Form',
            icon: 'close',
            hotkey: 'escape',
            onClick: () => {
                this.closeScreen();
            }
        });
        return items;
    }
    resetZIndexes() {
        const max = this.ScreenManager.screensList().length;
        let i = 1;
        this.ScreenManager.forEachScreen((screen) => {
            if (screen === this.ScreenManager.activeScreenGet()) {
                screen.rootElement.style.zIndex = `${max}`;
                Screen._currentZIndex = max;
            }
            else {
                screen.rootElement.style.zIndex = `${i}`;
                i++; // only increment when we're not setting the active screen
            }
        });
    }
    destroy() {
        if (this.rootElement) {
            const resizeMgr = DIContainer.getInstance('ResizeManager');
            resizeMgr.unobserve(this.rootElement);
        }
        super.destroy();
    }
}
Screen.VERSION = '$Revision: 129342 $';
/** The current z-index of the screen. It is shared across all screens. */
Screen._currentZIndex = 1;
